import type { StorefrontEvent } from './storefrontEvents';

export type ConsumerEventType = StorefrontEvent
  | 'search_submitted'
  | 'search_suggestion_clicked'
  | 'product_view'
  | 'product_shared'
  | 'support_chat_opened'
  | 'callback_requested'
  | 'checkout_started'
  | 'checkout_completed'
  | 'payment_initiated'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'order_created'
  | 'cancellation_requested'
  | 'cancellation_completed'
  | 'redemption_cta_clicked'
  | 'redemption_method_selected'
  | 'redemption_submitted'
  | 'redemption_success'
  | 'pay4me_intent_clicked'
  | 'pay_early_clicked'
  | 'pay4me_campaign_created'
  | 'pay4me_share_clicked'
  | 'points_balance_viewed'
  | 'points_redemption_selected'
  | 'points_redemption_removed'
  | 'repscore_opened'
  | 'repscore_loaded'
  | 'relief_recommendation_shown'
  | 'relief_confirmed'
  | 'relief_support_review_requested'
  | 'relief_resumed'
  | 'cap_hit'
  | 'cancel_proceeded_anyway'
  | 'manual_paid_during_relief'
  | 'recovery_recommendation_shown'
  | 'recovery_option_previewed'
  | 'recovery_option_accepted'
  | 'recovery_proceeded_to_cancel';

interface ConsumerEventInput {
  eventType: ConsumerEventType;
  source?: string;
  partnerCode?: string;
  sessionId?: string;
  orderId?: string;
  checkoutSessionId?: string;
  paymentReference?: string;
  /**
   * Optional idempotency key for event dedupe across retries or multiple call-sites.
   * When provided, the same `event_id` will be reused for the lifetime of the browser session.
   */
  dedupeKey?: string;
  properties?: Record<string, unknown>;
}

interface ConsumerEventPayload {
  event_id: string;
  event_type: ConsumerEventType;
  source: string;
  partner_code?: string;
  session_id?: string;
  order_id?: string;
  checkout_session_id?: string;
  payment_reference?: string;
  client_created_at: string;
  properties?: Record<string, string | number | boolean>;
}

const INGEST_ENDPOINT = '/api/proxy/v1/analytics/events/ingest';

const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function generateUuidV4(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
      bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10

      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
        .slice(6, 8)
        .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
    }
  }

  // Final fallback. This should be rare in modern browsers, but we prefer an RFC4122 UUID
  // because the backend ingestion endpoint validates `event_id` as a UUID.
  const randomHex = (length: number) =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');

  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${(
    8 + Math.floor(Math.random() * 4)
  ).toString(16)}${randomHex(3)}-${randomHex(12)}`;
}

function stableEventIdKey(dedupeKey: string): string {
  return `motito.analytics.event_id:${dedupeKey}`;
}

function stableEventId(dedupeKey: string): string {
  if (typeof window === 'undefined') {
    return generateUuidV4();
  }

  const storageKey = stableEventIdKey(dedupeKey);
  try {
    const cached = window.sessionStorage.getItem(storageKey);
    if (cached && UUID_V4_RE.test(cached)) {
      return cached;
    }
  } catch {
    // Ignore storage quota/privacy mode failures.
  }

  const created = generateUuidV4();
  try {
    window.sessionStorage.setItem(storageKey, created);
  } catch {
    // Ignore storage quota/privacy mode failures.
  }
  return created;
}

function defaultDedupeKey(input: ConsumerEventInput): string | null {
  if (input.eventType === 'checkout_started' && input.checkoutSessionId) {
    return `checkout_started:${input.checkoutSessionId}`;
  }
  if (input.eventType === 'order_created' && input.orderId) {
    return `order_created:${input.orderId}`;
  }
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const encoded = encodeURIComponent(name);
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const cookie = part.trim();
    if (!cookie.startsWith(`${encoded}=`)) {
      continue;
    }
    const value = cookie.slice(encoded.length + 1);
    try { return decodeURIComponent(value); } catch { return null; }
  }
  return null;
}

function defaultPartnerCode(): string {
  const partner = readCookie('partner_code');
  return partner ? partner.toUpperCase() : '';
}

function sanitizeProperties(
  rawProperties: Record<string, unknown> | undefined
): Record<string, string | number | boolean> | undefined {
  if (!rawProperties || typeof rawProperties !== 'object') {
    return undefined;
  }
  const sanitized: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(rawProperties)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string') {
      const compact = value.trim();
      if (!compact) {
        continue;
      }
      sanitized[key] = compact.slice(0, 200);
      continue;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
      continue;
    }
    if (typeof value === 'boolean') {
      sanitized[key] = value;
    }
  }
  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

function buildEventPayload(input: ConsumerEventInput): ConsumerEventPayload {
  const dedupeKey = (input.dedupeKey || defaultDedupeKey(input) || '').trim();
  return {
    event_id: dedupeKey ? stableEventId(dedupeKey) : generateUuidV4(),
    event_type: input.eventType,
    source: input.source ?? 'marketplace.web',
    partner_code: input.partnerCode || defaultPartnerCode() || undefined,
    session_id: input.sessionId,
    order_id: input.orderId,
    checkout_session_id: input.checkoutSessionId,
    payment_reference: input.paymentReference,
    client_created_at: new Date().toISOString(),
    properties: sanitizeProperties(input.properties),
  };
}

function sendWithBeacon(serializedPayload: string): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }
  if (serializedPayload.length > 60000) {
    return false;
  }
  const body = new Blob([serializedPayload], { type: 'application/json' });
  return navigator.sendBeacon(INGEST_ENDPOINT, body);
}

export function trackConsumerEvent(input: ConsumerEventInput): void {
  if (typeof window === 'undefined') {
    return;
  }

  const payload = {
    events: [buildEventPayload(input)],
  };
  const serializedPayload = JSON.stringify(payload);

  try {
    if (sendWithBeacon(serializedPayload)) return;
  } catch {
    // Some browser privacy modes reject sendBeacon synchronously; try fetch.
  }

  void fetch(INGEST_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: true,
    body: serializedPayload,
  }).catch(() => {
    // Telemetry must never block user flows.
  });
}
